/**
 * KAN-834: блок "Приложение" в карточке менеджера: "Ссылка для входа" выдаёт
 * одноразовую ссылку входа в приложение менеджера (PWA), её можно
 * скопировать, рядом срок действия. Виден владельцу и оператору
 * (canSetSeniorManager), менеджеру блока нет и запросов нет. Менеджер в
 * архиве: ссылку не выдаём. Логика в useManagerAppLink.
 * KAN-1000: тарифов нет, блок виден всегда (без строки про тарифы).
 */
import { useState } from 'react';
import { Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { copyToClipboard } from '@/hooks/useManagerBot';
import { useManagerAppLink } from '@/hooks/useManagerAppLink';
import { formatAttentionDate } from '@/components/dashboard/attentionFormat';
import { canSetSeniorManager } from '@/lib/managerAccess';

export const MANAGER_APP_LINK_HINT = 'Отправьте менеджеру, ссылка одноразовая, действует 3 дня';
export const MANAGER_APP_ARCHIVED_HINT = 'Менеджер в архиве, ссылку для входа выдать нельзя';

interface Props {
  managerId: string;
  isActive?: boolean;
}

export function ManagerAppBlock(props: Props): JSX.Element | null {
  const { data: user } = useCurrentUser();
  if (!canSetSeniorManager(user)) return null;
  return <ManagerAppLinkBlock {...props} />;
}

function ManagerAppLinkBlock({ managerId, isActive = true }: Props): JSX.Element {
  const { create, link, error } = useManagerAppLink(managerId);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (link) setCopied(await copyToClipboard(link.url));
  };

  return (
    <div className="space-y-2 rounded-md border border-gray-200 p-4" data-testid="manager-app-block">
      <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Smartphone className="h-4 w-4 text-gray-400" aria-hidden="true" />
        Приложение
      </h3>
      {!isActive ? (
        <p className="text-sm text-gray-600">{MANAGER_APP_ARCHIVED_HINT}</p>
      ) : (
        <>
          {link && (
            <div className="flex flex-wrap items-center gap-2">
              <code className="break-all rounded bg-gray-100 px-2 py-1 text-xs" data-testid="manager-app-link">
                {link.url}
              </code>
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                Копировать
              </Button>
              {copied && <span className="text-xs text-green-700">Скопировано</span>}
            </div>
          )}
          {link && (
            <p className="text-xs text-gray-600" data-testid="manager-app-link-expires">
              Действует до {formatAttentionDate(link.expires_at)}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={create.isPending}
            onClick={() => {
              setCopied(false);
              create.mutate();
            }}
          >
            {link ? 'Новая ссылка' : 'Ссылка для входа'}
          </Button>
          <p className="text-xs text-gray-500">{MANAGER_APP_LINK_HINT}</p>
        </>
      )}
      {error && (
        <p className="text-xs text-red-700" data-testid="manager-app-error">
          {error}
        </p>
      )}
    </div>
  );
}
